import React, { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { t, Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import Image from "next/image";
import { CardMedia, Divider, List, ListItem, ListItemButton, ListItemText } from "@mui/material";
import Button from "@mui/material/Button";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import IconButton from "@mui/material/IconButton";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useAlertStore } from "@/components/context/alert-store";
import { useGiftStore } from "@/components/context/gift-store";
import { ApiResponse } from "@/types/api";
import {
  IGift,
  IGiftCardImages,
  IGiftProduct,
  IGiftProductCategories,
  IGiftProductCategoriesModels,
} from "@/types/gift";

interface GiftImageProps {
  data: ApiResponse<IGiftProduct>;
  gift: IGift;
  imagePath: { current: string };
  isPending: boolean;
}

const GiftImage: React.FC<GiftImageProps> = ({ data, imagePath, gift, isPending }) => {
  const {
    productCard,
    previewUploadedFile,
    uploadedFile,
    setProductCard,
    setPreviewUploadedFile,
    setUploadedFile,
    setBlob,
    setScrollFromImage,
    selectedCategory,
    setSelectedCategory,
    selectedBackground,
    setSelectedBackground,
  } = useGiftStore();

  const { setMessage } = useAlertStore();
  const { i18n } = useLingui();

  const [nrOfBackgrounds, setNrOfBackgrounds] = useState<number>(4);

  const { options, showCategories, showUpload, productImages } = useMemo(() => {
    if (data?.success) {
      const options = Object.keys(data?.data?.categories).map(
        (key) => data?.data?.categories[Number(key)],
      );
      const productImages = data?.data?.images;
      imagePath.current = data?.data?.image_path;
      if (!gift) {
        if (productImages.length) setProductCard(productImages[0]);

        if (options.length) {
          setSelectedCategory(options[0]);
          if (options[0].models.length) {
            setSelectedBackground(options[0].models[0]);
          }
        }
      }
      return {
        options,
        showCategories: !data?.data?.hide_categories_and_models,
        showUpload: !data?.data?.disable_upload,
        productImages,
      };
    }
    return {
      options: [],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const removeUploadedFile = () => {
    if (productImages && productImages.length) setProductCard(productImages[0]);
    setPreviewUploadedFile("");
    setUploadedFile({ type: "", name: "" });
  };
  let index = 0;

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFile = e.target.files[0];
      if (selectedFile) {
        if (selectedFile.size > 5242880) {
          setMessage({
            text: t(i18n)`File is too large! The uploaded file size must be less than 5MB`,
            severity: "error",
          });
        } else {
          setPreviewUploadedFile(URL.createObjectURL(selectedFile));
          setUploadedFile(selectedFile);
          setBlob(selectedFile);
          setProductCard({ src: "", name: "", id: "" });
        }
      }
    }
  };
  return (
    <>
      {isPending && (
        <Box className="flex min-h-[200px] items-center justify-center">
          <Image src="/loader.gif" width="30" height="30" alt="Loader" loading="lazy" />
        </Box>
      )}
      {!isPending && showCategories && (
        <Box>
          <Box>
            <Typography
              sx={{ fontSize: "17px", color: "#000", marginBottom: "20px", fontWeight: "bold" }}
            >
              <Trans>Choose a theme</Trans>
            </Typography>
          </Box>

          {options.length === 0 && (
            <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
              <Trans>No options available</Trans>
            </Typography>
          )}
          {options.length > 0 && (
            <Box>
              <List
                sx={{
                  display: "flex",
                  alignContent: "flex-start",
                  flexWrap: "wrap",
                  gap: "5px",
                }}
              >
                {options.map((category: IGiftProductCategories) => (
                  <ListItem
                    key={category.category_id}
                    disablePadding
                    sx={{
                      display: "inline",
                      width: "auto",
                    }}
                  >
                    <ListItemButton
                      disableRipple
                      onClick={() => setSelectedCategory(category)}
                      component="button"
                      sx={{
                        cursor: "pointer",
                        borderRadius: "18px",
                        borderWidth: "1px",
                        borderStyle: "solid",
                        padding: "2px",
                        width: "auto",
                        color:
                          selectedCategory.category_id == category.category_id ? "#fff" : "#000",
                        borderColor: "rgb(188, 191, 193)",
                        transition: "0.3s ease",
                        backgroundColor:
                          selectedCategory.category_id == category.category_id
                            ? "primary.main"
                            : "transparent",
                        "&:hover": {
                          color:
                            selectedCategory.category_id == category.category_id
                              ? "#fff"
                              : "primary.main",
                          backgroundColor:
                            selectedCategory.category_id == category.category_id
                              ? "primary.main"
                              : "transparent",
                        },
                      }}
                    >
                      <ListItemText
                        primary={category.name}
                        sx={{
                          margin: "3px 12px 4px",
                          " .MuiTypography-root": {
                            fontWeight: "500",
                            fontSize: "13px",
                            lineHeight: "22px",
                          },
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
              {selectedCategory && selectedCategory.models.length > 0 && (
                <Box>
                  <List
                    sx={{
                      display: "flex",
                      alignContent: "flex-start",
                      flexWrap: "wrap",
                      marginTop: "10px",
                    }}
                  >
                    {selectedCategory.models.map((category: IGiftProductCategoriesModels) => {
                      index += 1;
                      if (index <= nrOfBackgrounds)
                        return (
                          <ListItem
                            key={category.model_id}
                            disablePadding
                            sx={{
                              marginBottom: "5px",
                              marginRight: "5px",
                              display: "inline",
                              width: "auto",
                            }}
                          >
                            <ListItemButton
                              disableRipple
                              onClick={() => setSelectedBackground(category)}
                              component="button"
                              sx={{
                                cursor: "pointer",
                                border:
                                  selectedBackground.model_id == category.model_id
                                    ? "solid 2px #0097ab"
                                    : "2px solid #fff",
                                margin: "1px 0",
                                padding: "2px",
                                width: "auto",
                                borderRadius: category.image ? "6px" : "50%",
                              }}
                            >
                              {category.image ? (
                                <Image
                                  loading="lazy"
                                  style={{
                                    width: "110px",
                                    maxHeight: "140px",
                                    borderRadius: "6px",
                                  }}
                                  src={`${imagePath.current}` + `${category.image}`}
                                  alt={category.name}
                                  width={80}
                                  height={55}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    backgroundImage: `linear-gradient(${category.angle}, ${category.hex_start} 0%, ${category.hex_end} 100%)`,
                                    width: "68px",
                                    height: "68px",
                                    borderRadius: "50%",
                                  }}
                                ></Box>
                              )}
                            </ListItemButton>
                          </ListItem>
                        );
                    })}
                  </List>
                  {nrOfBackgrounds < selectedCategory.models.length && (
                    <Button
                      sx={{
                        fontSize: "17px",
                        width: "100%",
                        textAlign: "right",
                        marginTop: "25px",
                        display: "block",
                      }}
                      onClick={() => setNrOfBackgrounds(nrOfBackgrounds + 4)}
                    >
                      <Trans>Show more</Trans>
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          )}
          <Divider sx={{ margin: "40px 0", borderColor: "#d2d2d7", width: "100%" }} />
        </Box>
      )}
      {showUpload && (
        <Box>
          <Box>
            <Typography
              sx={{ fontSize: "17px", color: "#000", marginBottom: "20px", fontWeight: "bold" }}
            >
              <Trans>Personalize your gift</Trans>
            </Typography>

            <Typography
              variant="subtitle1"
              sx={{ fontSize: "17px", color: "#1f1f25", marginBottom: "20px" }}
            >
              <Trans>
                Upload a video or photo to send with your gift card. The video/photo will be
                displayed above your theme.
              </Trans>
            </Typography>
          </Box>
          <Button
            component="label"
            variant="outlined"
            startIcon={<PhotoCameraIcon />}
            sx={{
              width: "100%",
              borderRadius: "6px",
              border: "solid 2px #1f1f25",
              backgroundColor: "transparent",
              height: "60px",
              marginTop: "5px",
              marginBottom: "15px",
              fontSize: "17px",
              color: "#000",
              "&:hover": {
                color: "primary.main",
                backgroundColor: "transparent",
                border: "solid 2px #1f1f25",
              },
            }}
          >
            <Trans>Upload image/video</Trans>
            <input
              type="file"
              accept="image/*,video/mp4,video/x-m4v"
              hidden
              onChange={(e) => handleFileInputChange(e)}
            />
          </Button>
          <Button
            component="label"
            variant="outlined"
            onClick={() => setScrollFromImage(true)}
            sx={{
              width: "100%",
              borderRadius: "6px",
              border: "solid 2px #1f1f25",
              backgroundColor: "transparent",
              height: "60px",
              marginTop: "5px",
              marginBottom: "15px",
              fontSize: "17px",
              color: "#000",
              "&:hover": {
                color: "primary.main",
                backgroundColor: "transparent",
                border: "solid 2px #1f1f25",
              },
            }}
          >
            <Trans>No, thanks</Trans>
          </Button>
          <Typography
            variant="subtitle1"
            sx={{
              fontSize: "11px",
              color: "rgba(0, 0, 0, 0.69)",
              marginBottom: "5px",
              marginTop: "5px",
              textAlign: "center",
            }}
          >
            <strong style={{ color: "#000" }}>
              <Trans>Hot tip</Trans>:
            </strong>
            &nbsp;
            <Trans>
              Landscape photos/videos (4:3 Format) work best. Please ensure your file size does not
              exceed the 100 MB limit.
            </Trans>
          </Typography>
          {previewUploadedFile && (
            <Box sx={{ display: "flex" }}>
              {uploadedFile?.type?.includes("image/") ? (
                <Box
                  sx={{
                    backgroundImage: `url(${previewUploadedFile})`,
                    maxWidth: "130px",
                    maxHeight: "90px",
                    width: "130px",
                    height: "90px",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    marginTop: "15px",
                  }}
                ></Box>
              ) : (
                <CardMedia
                  sx={{
                    maxWidth: "130px",
                    maxHeight: "90px",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                  component="video"
                  autoPlay
                  image={previewUploadedFile}
                />
              )}
              <Typography
                variant="subtitle1"
                sx={{
                  fontSize: "14px",
                  wordWrap: "break-word",
                  margin: "20px 20px",
                  color: "rgba(0, 0, 0, 0.69)",
                  textAlign: "center",
                }}
              >
                {uploadedFile?.name}
              </Typography>
              <IconButton
                disableRipple
                aria-label="delete"
                size="small"
                onClick={() => removeUploadedFile()}
                sx={{
                  broderRadius: "100%",
                  bgcolor: "#eee",
                  height: "40px",
                  width: "40px",
                  color: "#999",
                  margin: "auto 0 auto auto",
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: "16" }} />
              </IconButton>
            </Box>
          )}
          <Box sx={{ margin: "40px 0", borderTop: "1px solid #d2d2d7", width: "100%" }}> </Box>
        </Box>
      )}
      {!isPending && productImages && productImages.length > 1 && (
        <Box>
          <Typography
            sx={{ fontSize: "17px", color: "#000", marginBottom: "20px", fontWeight: "bold" }}
          >
            <Trans>Choose a card</Trans>
          </Typography>
          <Box>
            <List
              sx={{
                display: "flex",
                alignContent: "flex-start",
                flexWrap: "wrap",
                marginTop: "10px",
              }}
            >
              {productImages.map((image: IGiftCardImages) => {
                return (
                  <ListItem
                    key={`product-image-select-${image.id}`}
                    disablePadding
                    sx={{
                      display: "inline",
                      width: "auto",
                    }}
                  >
                    <ListItemButton
                      disableRipple
                      onClick={() => setProductCard(image)}
                      component="button"
                      sx={{
                        p: "5px",
                        cursor: "pointer",
                        margin: "2px",
                        borderRadius: "6px",
                        border:
                          productCard.name == image.name ? "solid 2px #0097ab" : "2px solid #fff",
                      }}
                    >
                      <Image
                        loading="lazy"
                        style={{
                          width: "182px",
                          maxHeight: "132px",
                          borderRadius: "6px",
                          margin: "-18px -22px -18px -22px",
                          maxWidth: "unset",
                        }}
                        src={`${image.src}`}
                        alt={image.name}
                        width={182}
                        height={132}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
          <Box sx={{ margin: "40px 0", borderTop: "1px solid #d2d2d7", width: "100%" }}> </Box>
        </Box>
      )}
    </>
  );
};

export default GiftImage;
